import React, { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import TagManager from "react-gtm-module";
import { ThemeProvider } from "styled-components/macro";
import { get, merge } from "lodash";

import Constants from "../constants";
import ModelSelectorContext from "../context/modelSelector/Context";
import { IntroConfig } from "../models/modelSelector/IntroConfig";
import { AppLoader } from "../components/Loader/AppLoader";
import { ModelSelector } from "../components/ModelSelector";
import client from "../api/client/SdsWebApiClient";
import {
    activateArticleNumber,
    filterSetsRecursive,
    getAccountId,
    getApplicationSetting,
    getApplicationSettings,
    getAppVersion,
    getConfigFile,
    getFrontendSettings,
    getLanguage,
    getStylingFile,
} from "../utilities/modelSelectorDataUtils";
import { BreakpointProvider } from "../utilities/media-js-breakpoints";
import defaultAppConfig from "../defaultAppConfig.json";
import { buildConfiguratorSearch } from "../utilities/navigationUtils";
import GaReporter from "../utilities/gaReporter";
import { StringPair } from "../api/generated";

function initGoogleTagManager(gtm_enabled: boolean, gtm_internal_id: string, gtm_external_id: string): void {
    if (!gtm_enabled) return;
    [gtm_internal_id, gtm_external_id].filter(Boolean).forEach(gtmId => TagManager.initialize({ gtmId }));
}

function ModelSelectorPage() {
    const navigate = useNavigate();
    const [settings, setSettings] = useState<StringPair[]>([]);
    const [introConfig, setIntroConfig] = useState<IntroConfig | undefined>();
    const [theme, setTheme] = useState<any>();
    const [initError, setInitError] = useState<{ stage: string; error: unknown } | null>(null);
    const [translatedTexts, setTranslatedTexts] = useState<Record<string, string>>({});
    const [isLoading, setLoading] = useState(true);
    const [account, setAccount] = useState<string | null>(null);
    const introConfigRef = useRef<IntroConfig | undefined>();
    const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);
    const isMountedRef = useRef(true);

    const handleDeepLinkingRedirect = useCallback(
        (searchParams: URLSearchParams): boolean => {
            const config = searchParams.get("config_string") || searchParams.get("encoded_config");
            if (!config) return false;
            const encoding = searchParams.get("enc_type") || undefined;
            const search = buildConfiguratorSearch(config, encoding);
            navigate(`${Constants.configuratorUrlPath}${search}`);
            return true;
        },
        [navigate]
    );

    const onSelectConfig = useCallback(
        (configString: string, configEncoding?: string, surfaceProductArticleNo?: string) => {
            const search = buildConfiguratorSearch(configString, configEncoding, surfaceProductArticleNo);
            navigate(`${Constants.configuratorUrlPath}${search}`);
        },
        [navigate]
    );

    const initGoogleAnalytics = async (
        frontendSettings: any,
        appVersion: string,
        accountId: string | null
    ): Promise<void> => {
        if (!frontendSettings.ga_tracking_enabled) return;
        const trackingIds = [frontendSettings.ga_common_measurement_id, frontendSettings.ga_measurement_id].filter(
            (id: string) => !!id
        );
        if (trackingIds.length === 0) return;

        const siteUrl = new URL(window.location.href);
        const cookiePath = siteUrl.pathname.replace(/\/[^/]+\/?$/i, "") || "/";
        const useCookies = (siteUrl.searchParams.get("allow_statistical_cookies") || "false").toLowerCase() === "true";
        const applicationType = siteUrl.searchParams.get("ga_application_type") || "Web";
        GaReporter.initTracking(trackingIds, useCookies, cookiePath, applicationType, appVersion);
        if (accountId) GaReporter.setAccountId(accountId);
        if (frontendSettings.ga_tracking_project) GaReporter.setProject(frontendSettings.ga_tracking_project);
    };

    useEffect(() => {
        const init = async () => {
            const insideIframe = window.self !== window.top;
            const url = new URL(window.location.href);

            if (insideIframe && document.referrer) {
                const referrerUrl = new URL(document.referrer);
                const configuratorReferrerRegex = new RegExp(
                    `${Constants.configuratorUrlPath.replace(/\//g, "\\/")}\\/?$`,
                    "i"
                );
                const refererIsConfigurator =
                    url.hostname.toUpperCase() === referrerUrl.hostname.toUpperCase() &&
                    configuratorReferrerRegex.test(referrerUrl.pathname);
                if (!refererIsConfigurator && handleDeepLinkingRedirect(referrerUrl.searchParams)) {
                    return;
                }
            }

            if (!insideIframe && handleDeepLinkingRedirect(url.searchParams)) {
                return;
            }

            const isTouchScreen = url.searchParams.get("touch_screen");
            setInitError(null);

            if (isTouchScreen && /true/i.test(isTouchScreen)) {
                window.addEventListener("contextmenu", (e: Event) => e.preventDefault());
            }

            let language: string;
            let accountId: string | null;
            try {
                const languagePromise = getLanguage();
                getApplicationSettings().then(response => setSettings(response));
                const accountIdPromise = getAccountId();
                language = await languagePromise;
                accountId = await accountIdPromise;
            } catch (e) {
                setInitError({ stage: "getLanguage/getAccountId", error: e });
                console.error("[ModelSelector] init failed at getLanguage/getAccountId", e);
                return;
            }

            let configurationFiles: Awaited<ReturnType<typeof client.getConfigurationFilesGet>>;
            try {
                configurationFiles = await client.getConfigurationFilesGet(language, accountId ?? undefined);
            } catch (e) {
                setInitError({ stage: "getConfigurationFilesGet", error: e });
                console.error("[ModelSelector] init failed at getConfigurationFilesGet", e);
                return;
            }

            getStylingFile(configurationFiles.stylingFile).then(appConfig => {
                const config = merge(defaultAppConfig, appConfig);
                const langTexts = get(config, `translations.${language}`, {});
                setTranslatedTexts(langTexts);
                setTheme(config.style);

                const r = document.documentElement;
                r.style.setProperty("--primary-color", config.colors.primaryColor);
                r.style.setProperty("--primary-hover-color", config.colors.primaryHoverColor);
                r.style.setProperty("--primary-active-color", config.colors.primaryActiveColor);
                r.style.setProperty("--light-hover-color", config.colors.lightHoverColor);
                r.style.setProperty("--light-active-color", config.colors.lightActiveColor);
            });

            const initFilePromise = getConfigFile(configurationFiles.initFile);
            const frontendSettingsPromise = getFrontendSettings();
            const appVersionPromise = getAppVersion();

            Promise.all([initFilePromise, frontendSettingsPromise, appVersionPromise])
                .then(async ([config, frontendSettings, appVersion]) => {
                    const settingsParam = url.searchParams.get("settings");
                    const setFilterRaw = settingsParam?.split(",") ?? [];
                    const setFilterUnique = Array.from(
                        new Map(
                            setFilterRaw
                                .map(s => s.trim())
                                .filter(s => s.length > 0)
                                .map(s => [s.toLowerCase(), s])
                        ).values()
                    );
                    const setFilter = setFilterUnique.length > 0 ? setFilterUnique : undefined;
                    const filteredConfig =
                        setFilter && setFilter.length > 0
                            ? { ...config, sets: filterSetsRecursive(config.sets, setFilter) }
                            : config;

                    setAccount(accountId);
                    introConfigRef.current = filteredConfig;

                    // eslint-disable-next-line no-console
                    console.log(`Model selector version: ${appVersion}`);

                    initGoogleTagManager(
                        frontendSettings.gtm_enabled,
                        frontendSettings.gtm_internal_id,
                        frontendSettings.gtm_external_id
                    );
                    await initGoogleAnalytics(frontendSettings, appVersion, accountId);
                    GaReporter.trackPageView();

                    const autoPickProduct = url.searchParams.get("surface_product");
                    if (
                        autoPickProduct &&
                        (await activateArticleNumber(
                            autoPickProduct,
                            filteredConfig,
                            accountId || undefined,
                            setFilter,
                            onSelectConfig
                        ))
                    ) {
                        return;
                    }

                    setIntroConfig(filteredConfig);

                    const handleMessage = (event: MessageEvent) => {
                        if (
                            event.data?.type === "ACTIVATE_BY_ARTICLE_NUMBER" &&
                            event.data.articleNumber &&
                            introConfigRef.current
                        ) {
                            activateArticleNumber(
                                event.data.articleNumber,
                                introConfigRef.current,
                                accountId || undefined,
                                setFilter,
                                onSelectConfig
                            );
                        }
                    };
                    if (isMountedRef.current) {
                        messageHandlerRef.current = handleMessage;
                        window.addEventListener("message", handleMessage);
                    }
                })
                .catch(e => {
                    setInitError({ stage: "getConfigFile/getFrontendSettings/getAppVersion", error: e });
                    console.error("[ModelSelector] init failed at getConfigFile/getFrontendSettings/getAppVersion", e);
                });
        };
        isMountedRef.current = true;
        init();
        return () => {
            isMountedRef.current = false;
            if (messageHandlerRef.current) {
                window.removeEventListener("message", messageHandlerRef.current);
                messageHandlerRef.current = null;
            }
        };
    }, [navigate, handleDeepLinkingRedirect, onSelectConfig]);

    if (initError) {
        const message = initError.error instanceof Error ? initError.error.message : String(initError.error);
        return (
            <div style={{ padding: 24, fontFamily: "sans-serif" }}>
                <p>
                    <strong>Model selector init failed at: {initError.stage}</strong>
                </p>
                <p style={{ color: "#c00" }}>{message}</p>
                <p style={{ fontSize: 12, marginTop: 8 }}>
                    Check the console for details. Ensure <code>service_url</code> points at your backend (e.g.
                    ?service_url=https://localhost:5001) and CORS allows this origin.
                </p>
            </div>
        );
    }

    if (!settings.length || !introConfig || !theme) {
        return (
            <AppLoader
                color={theme ? get(theme, "appLoader.color", "var(--primary-color)") : "#888"}
                background={get(theme, "appLoader.backgroundColor", "white")}
                height={200}
                width={80}
            />
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <BreakpointProvider>
                <ModelSelectorContext.Provider
                    value={{
                        translatedTexts,
                        isLoading,
                        setIsLoading: setLoading,
                    }}
                >
                    {isLoading ? (
                        <AppLoader
                            color={get(theme, "appLoader.color", "var(--primary-color)")}
                            background={get(theme, "appLoader.backgroundColor", "white")}
                            height={200}
                            width={80}
                        />
                    ) : null}
                    <div style={{ background: get(theme, "modelSelector.backgroundColor", "white") }}>
                        <ModelSelector
                            composedUrl={getApplicationSetting(settings, "application.composed_url")}
                            guiResourcesUrl={getApplicationSetting(settings, "application.gui_url")}
                            introConfig={introConfig}
                            accountId={account}
                            onSelectConfig={onSelectConfig}
                        />
                    </div>
                </ModelSelectorContext.Provider>
            </BreakpointProvider>
        </ThemeProvider>
    );
}

export default ModelSelectorPage;
